import { useEffect, useRef, useState } from 'react';
import './ProgramExplorer.css';
import Icon from './Icon';
import useReveal from './useReveal';

const ROTATE_MS = 5000;

// Interactive program explorer — a self-advancing tab panel with a
// pointer-tracked spotlight, a giant ghost numeral and drifting aurora.
// Shared by For Corporates / Colleges / Individuals; `variant` switches skins.
export default function ProgramExplorer({
  id,
  variant = 'dark',
  eyebrow,
  title,
  intro,
  programs,
  ctaText,
  ctaHref = '#contact',
  ctaClass = 'btn-primary',
}) {
  const headerRef = useReveal();
  const tabsRef = useReveal();
  const stageRevealRef = useReveal();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const stageRef = useRef(null);

  // Auto-advance; pauses on hover/focus and honours reduced-motion. Any manual
  // selection restarts the dwell via the `active` dependency.
  useEffect(() => {
    if (paused) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const t = setTimeout(
      () => setActive((i) => (i + 1) % programs.length),
      ROTATE_MS
    );
    return () => clearTimeout(t);
  }, [active, paused, programs.length]);

  const onMove = (e) => {
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
    el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
  };

  const program = programs[active];

  return (
    <section id={id} className={`explorer explorer--${variant}`}>
      <div className="explorer__aurora" aria-hidden="true">
        <span className="explorer__blob explorer__blob--1" />
        <span className="explorer__blob explorer__blob--2" />
      </div>

      <div className="container explorer__inner">
        <header className="explorer__header" ref={headerRef}>
          <p className="eyebrow explorer__eyebrow">{eyebrow}</p>
          <h2 className="explorer__title">{title}</h2>
          <p className="explorer__intro">{intro}</p>
        </header>

        <div
          className="explorer__panel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
        >
          <div className="explorer__tabs" role="tablist" aria-label={title} ref={tabsRef}>
            {programs.map((p, i) => (
              <button
                key={p.title}
                type="button"
                role="tab"
                id={`${id}-tab-${i}`}
                aria-selected={i === active}
                aria-controls={`${id}-stage`}
                className={`explorer__tab${i === active ? ' is-active' : ''}`}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
              >
                <span className="explorer__tab-icon" aria-hidden="true">
                  <Icon name={p.icon} size={20} strokeWidth={1.8} />
                </span>
                <span className="explorer__tab-text">{p.title}</span>
                {i === active && !paused && (
                  <span className="explorer__tab-progress" aria-hidden="true" />
                )}
              </button>
            ))}
          </div>

          <div className="explorer__stage-outer reveal--scale" ref={stageRevealRef}>
            <div
              className="explorer__stage"
              id={`${id}-stage`}
              role="tabpanel"
              aria-labelledby={`${id}-tab-${active}`}
              ref={stageRef}
              onMouseMove={onMove}
            >
              <div className="explorer__stage-glow" aria-hidden="true" />
              <div className="explorer__stage-content" key={active}>
                <span className="explorer__stage-icon" aria-hidden="true">
                  <Icon name={program.icon} size={30} strokeWidth={1.6} />
                </span>
                <h3 className="explorer__stage-title">{program.title}</h3>
                <p className="explorer__stage-desc">{program.desc}</p>
                <ul className="explorer__stage-tags">
                  {program.tags.map((t) => (
                    <li className="explorer__stage-tag" key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <a href={ctaHref} className={`btn ${ctaClass} explorer__cta`}>{ctaText}</a>
      </div>
    </section>
  );
}
